import type * as React from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@cloudscape-design/components/button';
import { trackEvents } from '@/lib/events';

type Props = {
  /** URL do deep-link (de `posthogLinks`). `null` quando o project id não
   *  está configurado — aí o botão não renderiza. */
  href: string | null;
  /** Identificador da superfície de origem, para telemetria. */
  surface: string;
};

/**
 * Botão "Abrir no PostHog" — ponto de entrada de drill-down ao vivo. Não
 * renderiza nada quando `href` é null, então as telas podem sempre incluí-lo
 * sem checar disponibilidade do PostHog. Abre em nova aba e registra
 * `bi_posthog_deeplink_opened`.
 *
 * Origem: #68 — 2026-06-20 13:06 +0200 (commit d7f994997, deep-links do BI).
 *
 * Contrato: `href` vem dos builders de `lib/posthogLinks.ts`; `surface` é o
 * nome do dashboard de origem (`product_dau`, `operation`…). Texto via i18n
 * `common.openInPostHog`.
 *
 * Falhas conhecidas: até 2026-10-13 `trackEvents.biPostHogDeeplinkOpened`
 * mandava esse valor na chave `surface`, a mesma da super property
 * `surface: 'web-admin'`; no posthog-js a propriedade do evento prevalece, e o
 * evento saía sem a marca `web-admin` (#936). Desde a #939 vai como
 * `dashboard`. A prop do componente continua `surface` — é só o nome local.
 *
 * Usado por (admin): `ProductDashboardPage`, `OperationDashboardPage`,
 * `AdminUsageDashboardPage`, `FunnelDashboardPage`, `CohortsDashboardPage`.
 *
 * Histórico:
 *  - 2026-10-10 15:12 +0200 — (#936) cabeçalho padrão (#796).
 *  - 2026-10-13 18:23 +0200 — (#939) evento manda `dashboard`, não `surface`.
 */
export function OpenInPostHogButton({ href, surface }: Props): React.JSX.Element | null {
  const { t } = useTranslation();
  if (href == null) return null;
  return (
    <Button
      href={href}
      target="_blank"
      iconName="external"
      iconAlign="right"
      onClick={(): void => {
        trackEvents.biPostHogDeeplinkOpened(surface);
      }}
    >
      {t('common.openInPostHog')}
    </Button>
  );
}
